import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import {
  UserAgent,
  Registerer,
  Inviter,
  SessionState,
  type Session,
  type Invitation,
} from 'sip.js';

import { useSipSettings, useUploadRecording } from '@/hooks/useSipSettings';
import { useCreateActivity } from '@/hooks/useActivities';
import { useProject } from '@/contexts/ProjectContext';

// ─── TYPES ────────────────────────────────────────────────────────

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'registered';
type CallState = 'idle' | 'ringing_out' | 'ringing_in' | 'in_call' | 'on_hold';

interface CallInfo {
  direction: 'inbound' | 'outbound';
  remoteNumber: string;
  contactId?: string;
  leadId?: string;
  startedAt?: Date;
}

interface SipContextType {
  // State
  connectionState: ConnectionState;
  callState: CallState;
  callInfo: CallInfo | null;
  isMuted: boolean;
  isRecording: boolean;
  callDuration: number;
  isConfigured: boolean;

  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  makeCall: (number: string, contactId?: string, leadId?: string) => void;
  hangup: () => void;
  answer: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  sendDtmf: (digit: string) => void;
}

const SipContext = createContext<SipContextType | undefined>(undefined);

// ─── PROVIDER ─────────────────────────────────────────────────────

export function SipProvider({ children }: { children: ReactNode }) {
  const { data: sipSettings } = useSipSettings();
  const createActivity = useCreateActivity();
  const uploadRecording = useUploadRecording();
  const { currentProject } = useProject();

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [callState, setCallState] = useState<CallState>('idle');
  const [callInfo, setCallInfo] = useState<CallInfo | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isRecording] = useState(true); // Always record
  const [callDuration, setCallDuration] = useState(0);

  const uaRef = useRef<UserAgent | null>(null);
  const registererRef = useRef<Registerer | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const isConfigured = !!(
    sipSettings?.enabled &&
    sipSettings?.wsUrl &&
    sipSettings?.username &&
    sipSettings?.domain
  );

  // ── Call duration timer ──

  useEffect(() => {
    if (callState === 'in_call') {
      setCallDuration(0);
      timerRef.current = setInterval(() => {
        setCallDuration((d) => d + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callState]);

  // ── Audio element for remote stream ──

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.autoplay = true;
    }
  }, []);

  // ── Setup session event handlers ──

  const setupSession = useCallback(
    (session: Session, direction: 'inbound' | 'outbound', remoteNumber: string) => {
      sessionRef.current = session;

      session.stateChange.addListener((state: SessionState) => {
        switch (state) {
          case SessionState.Establishing:
            setCallState(direction === 'outbound' ? 'ringing_out' : 'ringing_in');
            break;

          case SessionState.Established: {
            setCallState('in_call');
            setCallInfo((prev) => prev ? { ...prev, startedAt: new Date() } : null);

            // Attach remote audio
            const remoteStream = new MediaStream();
            const pc = (session.sessionDescriptionHandler as any)?.peerConnection as RTCPeerConnection | undefined;
            if (pc) {
              pc.getReceivers().forEach((receiver) => {
                if (receiver.track) remoteStream.addTrack(receiver.track);
              });
              if (audioRef.current) {
                audioRef.current.srcObject = remoteStream;
              }

              // Start recording — combine local + remote
              try {
                const localStream = pc.getSenders()
                  .filter((s) => s.track)
                  .reduce((stream, sender) => {
                    stream.addTrack(sender.track!);
                    return stream;
                  }, new MediaStream());

                const audioCtx = new AudioContext();
                const dest = audioCtx.createMediaStreamDestination();
                audioCtx.createMediaStreamSource(remoteStream).connect(dest);
                audioCtx.createMediaStreamSource(localStream).connect(dest);

                const recorder = new MediaRecorder(dest.stream, {
                  mimeType: 'audio/webm;codecs=opus',
                });
                chunksRef.current = [];
                recorder.ondataavailable = (e) => {
                  if (e.data.size > 0) chunksRef.current.push(e.data);
                };
                recorder.start(1000);
                recorderRef.current = recorder;
              } catch (err) {
                console.warn('[SIP] Recording failed to start:', err);
              }
            }
            break;
          }

          case SessionState.Terminated: {
            // Stop recording & upload
            const duration = callDuration;
            const info = callInfo;

            if (recorderRef.current && recorderRef.current.state !== 'inactive') {
              recorderRef.current.stop();
              recorderRef.current.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                if (blob.size > 0 && info) {
                  try {
                    // Create activity first
                    const activity = await createActivity.mutateAsync({
                      type: 'call',
                      subject: `Hovor ${info.direction === 'outbound' ? '→' : '←'} ${info.remoteNumber}`,
                      contactId: info.contactId,
                      leadId: info.leadId,
                      meta: {
                        duration,
                        callerNumber: sipSettings?.callerId,
                        calleeNumber: info.remoteNumber,
                        callDirection: info.direction,
                        callStatus: 'completed',
                      },
                    });

                    // Upload recording and link to activity
                    await uploadRecording.mutateAsync({
                      blob,
                      activityId: activity.id,
                      fileName: `call_${info.remoteNumber}_${Date.now()}.webm`,
                    });
                  } catch (err) {
                    console.error('[SIP] Failed to save call activity:', err);
                  }
                }
                chunksRef.current = [];
                recorderRef.current = null;
              };
            } else if (info) {
              // No recording but still log the activity
              createActivity.mutate({
                type: 'call',
                subject: `Hovor ${info.direction === 'outbound' ? '→' : '←'} ${info.remoteNumber}`,
                contactId: info.contactId,
                leadId: info.leadId,
                meta: {
                  duration,
                  callerNumber: sipSettings?.callerId,
                  calleeNumber: info.remoteNumber,
                  callDirection: info.direction,
                  callStatus: duration > 0 ? 'completed' : 'missed',
                },
              });
            }

            // Reset state
            setCallState('idle');
            setCallInfo(null);
            setCallDuration(0);
            setIsMuted(false);
            sessionRef.current = null;
            if (audioRef.current) audioRef.current.srcObject = null;
            break;
          }
        }
      });
    },
    [callDuration, callInfo, sipSettings, createActivity, uploadRecording],
  );

  // ── Connect to SIP server ──

  const connect = useCallback(async () => {
    if (!sipSettings?.wsUrl || !sipSettings?.username || !sipSettings?.domain) return;

    setConnectionState('connecting');

    try {
      const uri = UserAgent.makeURI(`sip:${sipSettings.username}@${sipSettings.domain}`);
      if (!uri) throw new Error('Invalid SIP URI');

      const ua = new UserAgent({
        uri,
        transportOptions: {
          server: sipSettings.wsUrl,
        },
        authorizationUsername: sipSettings.username,
        authorizationPassword: sipSettings.password || '',
        displayName: sipSettings.callerId || sipSettings.username,
        delegate: {
          onInvite: (invitation: Invitation) => {
            const remoteNumber = invitation.remoteIdentity.uri.user || 'Neznámé číslo';
            setCallInfo({
              direction: 'inbound',
              remoteNumber,
            });
            setCallState('ringing_in');
            setupSession(invitation, 'inbound', remoteNumber);
          },
        },
      });

      await ua.start();
      setConnectionState('connected');
      uaRef.current = ua;

      const registerer = new Registerer(ua);
      await registerer.register();
      setConnectionState('registered');
      registererRef.current = registerer;
    } catch (err) {
      console.error('[SIP] Connection failed:', err);
      setConnectionState('disconnected');
    }
  }, [sipSettings, setupSession]);

  // ── Disconnect ──

  const disconnect = useCallback(() => {
    registererRef.current?.unregister().catch(() => {});
    uaRef.current?.stop().catch(() => {});
    uaRef.current = null;
    registererRef.current = null;
    setConnectionState('disconnected');
  }, []);

  // ── Auto-connect when settings change ──

  useEffect(() => {
    if (isConfigured && connectionState === 'disconnected') {
      connect();
    }
    return () => {
      if (uaRef.current) disconnect();
    };
  }, [isConfigured]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Make outbound call ──

  const makeCall = useCallback(
    (number: string, contactId?: string, leadId?: string) => {
      if (!uaRef.current || !sipSettings?.domain) return;
      if (callState !== 'idle') return;

      const target = UserAgent.makeURI(`sip:${number}@${sipSettings.domain}`);
      if (!target) return;

      setCallInfo({
        direction: 'outbound',
        remoteNumber: number,
        contactId,
        leadId,
      });

      const inviter = new Inviter(uaRef.current, target);
      setupSession(inviter, 'outbound', number);
      inviter.invite();
    },
    [sipSettings, callState, setupSession],
  );

  // ── Hangup ──

  const hangup = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;

    switch (session.state) {
      case SessionState.Initial:
      case SessionState.Establishing:
        if ('cancel' in session) (session as Inviter).cancel();
        else if ('reject' in session) (session as Invitation).reject();
        break;
      case SessionState.Established:
        session.bye();
        break;
    }
  }, []);

  // ── Answer incoming call ──

  const answer = useCallback(() => {
    const session = sessionRef.current;
    if (session && 'accept' in session) {
      (session as Invitation).accept();
    }
  }, []);

  // ── Mute ──

  const toggleMute = useCallback(() => {
    const pc = (sessionRef.current?.sessionDescriptionHandler as any)?.peerConnection as RTCPeerConnection | undefined;
    if (pc) {
      pc.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'audio') {
          sender.track.enabled = isMuted; // Toggle
        }
      });
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // ── Hold (simplified — re-invite with sendonly) ──

  const toggleHold = useCallback(() => {
    // Hold/unhold via session.hold()/unhold() is complex in SIP.js
    // For MVP, we just mute both directions
    toggleMute();
  }, [toggleMute]);

  // ── DTMF ──

  const sendDtmf = useCallback((digit: string) => {
    const session = sessionRef.current;
    if (session && session.state === SessionState.Established) {
      const pc = (session.sessionDescriptionHandler as any)?.peerConnection as RTCPeerConnection | undefined;
      if (pc) {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender && sender.dtmf) {
          sender.dtmf.insertDTMF(digit, 100, 70);
        }
      }
    }
  }, []);

  return (
    <SipContext.Provider
      value={{
        connectionState,
        callState,
        callInfo,
        isMuted,
        isRecording,
        callDuration,
        isConfigured,
        connect,
        disconnect,
        makeCall,
        hangup,
        answer,
        toggleMute,
        toggleHold,
        sendDtmf,
      }}
    >
      {children}
    </SipContext.Provider>
  );
}

// ─── HOOK ─────────────────────────────────────────────────────────

export function useSip() {
  const context = useContext(SipContext);
  if (!context) {
    throw new Error('useSip must be used within SipProvider');
  }
  return context;
}
